<template>
    <div class="payslip-container payslip-ref">
        <div v-if="this.employee" class="payslip" ref="payslip">
            <!-- Payslip Header -->
            <div class="payslip-header">
                <div class="company-info">
                    <div>
                        <img v-if="companyInfo.logo" :src="companyInfo.logo" width="150px" height="auto"
                            :alt="companyInfo.name">
                        <h2 v-else class="company-name">{{ companyInfo.name }}</h2>
                        <div style="margin-top: 10px">
                            <p v-if="companyInfo.address" class="company-details">{{ companyInfo.address }}</p>
                            <p v-if="companyContact" class="company-contact">{{ companyContact }}</p>
                        </div>
                    </div>
                </div>
                <div class="payslip-title">
                    <h1>PAYSLIP</h1>
                    <p class="payslip-period">{{ formatPeriod(this.employee.month, this.employee.year) }}</p>
                    <p class="payslip-id">Transaction #{{ this.formatPayrollId(this.employee.payrollId) || generateTransactionId() }}</p>
                </div>
            </div>

            <!-- Employee Information -->
            <div class="employee-info">
                <div class="employee-details">
                   <div class="section-header" style="margin-bottom: 20px">
                        <h3>Employee Details</h3>
                        <div class="header-line"></div>
                    </div>
                   
                    <div class="detail-row">
                        <span class="detail-label">Employee ID:</span>
                        <span class="detail-value">{{ this.formatEmployeeId(this.employee.employeeId) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Name:</span>
                        <span class="detail-value">{{ this.employee.employeeName }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Designation:</span>
                        <span class="detail-value">{{ this.employee.employeeDesignation || 'Not specified' }}</span>
                    </div>
                </div>
                <div class="payment-details">
                    <div class="section-header" style="margin-bottom: 20px">
                        <h3>Payment Details</h3>
                        <div class="header-line"></div>
                    </div>
                    
                    <div class="detail-row">
                        <span class="detail-label">Payment Date:</span>
                        <span class="detail-value">{{ paymentDate }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Status:</span>
                        <span class="detail-value status-badge"
                            :class="this.employee.status && this.employee.status.toLowerCase()">
                            {{ this.employee.status }}
                        </span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Payment Method:</span>
                        <span class="detail-value">Bank Transfer</span>
                    </div>
                </div>
            </div>

            <!-- salary calculation -->
            <div class="calculation-breakdown">
                <div class="section-header" >
                    <h3>Earnings</h3>
                    <div class="header-line"></div>
                </div>

                <!-- Total Days Salary -->

                <div class="calculation-row">
                    <span class="calculation-label">Basic Salary</span>
                    <span class="calculation-value">{{ formatCurrency(this.employee.basicSalary) }}</span>
                </div>

                <!-- Late Deduction -->
                <div v-if="this.employee.lateDeduction > 0" class="calculation-row">
                    <span class="calculation-label">Allowances</span>
                    <span class="calculation-value">{{ formatCurrency(this.employee.extraAllowance) }}</span>
                </div>
            </div>

            <!-- Earnings & Deductions -->
            <div class="salary-details">
                <div class="earnings">
                    <div class="section-header">
                        <h3>Salary Calculations</h3>
                        <div class="header-line"></div>
                    </div>
                    <div class="calculation-row">
                        <span class="amount-label">Total Days Salary (PKR)</span>
                        <span class="calculation-value">{{ formatCurrency(this.salaryData.totalDaysSalary) }}</span>
                    </div>
                    <div class="calculation-note">
                        {{ this.employee.perDaySalary }} * ({{ this.employee.daysWorked }} Days)
                    </div>
                    <div v-if="this.employee.bonus > 0" class="amount-row">
                        <span class="amount-label">Bonus/Incentives</span>
                        <span class="amount-value">{{ formatCurrency(this.employee.bonus) }}</span>
                    </div>
                    <div v-if="this.employee.leavesEncashmentAmount > 0" class="amount-row">
                        <span class="amount-label">Leave Encashment ({{ totalEncashmentDays }}
                            days)</span>
                        <span class="amount-value">{{ formatCurrency(this.employee.leavesEncashmentAmount || this.salaryData.leavesEncashmentDays) }}</span>
                    </div>
                    <div v-if="this.employee.extraAmountAdjustment > 0" class="calculation-row">
                        <span class="amount-label">Adjustments (PKR)</span>
                        <span class="calculation-value">{{
                            formatCurrency(this.employee.extraAmountAdjustment) }}</span>
                    </div>
                    <div v-if=" this.employee.extraAmountAdjustment > 0 && this.employee.extraAmountAdjustmentNote && this.employee.extraAmountAdjustment" class="calculation-note">
                        {{ this.employee.extraAmountAdjustmentNote }}
                    </div>
                    <div v-if="this.employee.transactionFees > 0" class="amount-row">
                        <span class="amount-label">Transaction Fees</span>
                        <span class="amount-value">{{ formatCurrency(this.employee.transactionFees) }}</span>
                    </div>
                    <div class="amount-row total">
                        <span class="amount-label">Gross Earnings</span>
                        <span class="amount-value">{{ formatCurrency(grossEarnings) }}</span>
                    </div>
                </div>

                <div class="deductions" style="position: relative;">
                    <div class="section-header">
                        <h3>Deductions</h3>
                        <div class="header-line"></div>
                    </div>

                    <div v-if="this.employee.lateDeduction > 0" class="calculation-row">
                        <span class="amount-label">Late Deduction (PKR)</span>
                        <span class="calculation-value">- {{ formatCurrency(this.employee.lateDeduction) }}</span>
                    </div>
                    <div v-if="this.employee.lateDeduction > 0" class="calculation-note">
                        ({{ formattedPerHoursalary }} / hr * {{ this.employee.lateDeductionDays * 4 }}x)
                    </div>

                    <div v-if="this.employee.extraAmountAdjustment < 0" class="calculation-row">
                        <span class="amount-label">Adjustments (PKR)</span>
                        <span class="calculation-value">  {{
                            formatCurrency(this.employee.extraAmountAdjustment) }} </span>
                    </div>
                     <div v-if=" this.employee.extraAmountAdjustment < 0 && this.employee.extraAmountAdjustmentNote && this.employee.extraAmountAdjustment" class="calculation-note">
                        {{ this.employee.extraAmountAdjustmentNote }}
                    </div>
                    <div v-if="this.employee.taxDeduction > 0" class="calculation-row">
                        <span class="amount-label">Tax Deduction (PKR)</span>
                        <span class="calculation-value">- {{ formatCurrency(this.employee.taxDeduction) }}</span>
                    </div>
                    <div class="amount-row total" :style="totalDeductions <= 0 ? { paddingTop: '5px' } : {}">
                        <span class="amount-label">Total Deductions</span>
                        <span class="calculation-value">- {{ formatCurrency(totalDeductions) }}</span>
                    </div>
                </div>
            </div>


            <!-- Net Pay -->
            <div class="net-pay">
                <div class="net-pay-inner">
                    <span class="net-pay-label">Net Pay</span>
                    <span class="net-pay-amount">{{ formatCurrency(this.employee.finalAmount) }}</span>
                </div>
                <p class="net-pay-words">({{ amountToWords(Number(this.employee.finalAmount)) }} only)</p>
            </div>

            <!-- Footer -->
            <div class="payslip-footer">
                <div class="footer-notes">
                    <p>• This is a system generated payslip and does not require signature</p>
                    <p>• Please report any discrepancies within 7 days of receipt</p>
                </div>
                <div class="footer-meta">
                    <p>Generated on: {{ currentDateTime }}</p>
                    <p>Software: {{ companyInfo.software }}</p>
                </div>
            </div>

            <!-- Action Buttons -->
        </div>
        <div class="payslip-actions" v-if="!hideActions">

            <button @click="downloadPayslip" class="action-btn download-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <path d="M12 15V3" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                Download PDF
            </button>
        </div>
        <div v-else class="no-data">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="#6B7280" stroke-width="2" />
                <path d="M12 8V12" stroke="#6B7280" stroke-width="2" stroke-linecap="round" />
                <path d="M12 16H12.01" stroke="#6B7280" stroke-width="2" stroke-linecap="round" />
            </svg>
            <h3>No Payroll Data Available</h3>
            <p>Unable to generate payslip without employee payroll information</p>
        </div>
    </div>
</template>

<script>
import jsPDF from 'jspdf';
import html2canvas from "html2canvas";
import { convertAmountToWords } from "../../../assets/config/helpers";
import { monthNames } from "../../../assets/config/constants";

export default {
    name: "payslip",
    props: {
        // Optional overrides; by default details come from Settings > Company Details
        company: {
            type: Object,
            default: () => ({}),
        },
        hideActions: {
            type: Boolean,
            default: false,
        },

    },
    data() {
        return {
            downloadedOnce: false,
            currentDateTime: new Date().toLocaleString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }),
            employee: {},
            salaryData: {}
        };
    },
    computed: {
        // `leavesQuota` only exists on June/December records, so default it.
        totalEncashmentDays() {
            return (Number(this.employee.leavesEncashmentDays) || 0) +
                (Number((this.employee.leavesQuota || {}).leavesEncashmentDays) || 0);
        },
        companyInfo() {
            const constants = this.$store.state.auth.constants || {};
            const company = this.$store.state.auth.company || {};
            return {
                name: company.companyName || "",
                address: constants.companyAddress || "",
                phone: constants.companyPhone || "",
                email: constants.companyEmail || "",
                logo: constants.companyLogoUrl || "",
                software: "Pertime",
                ...this.company,
            };
        },
        companyContact() {
            return [this.companyInfo.phone, this.companyInfo.email].filter(Boolean).join(" • ");
        },
        formattedPerHoursalary() {
            return Number((this.employee.perDaySalary / this.employee.empTotalHours)).toFixed(2);
        },
        paymentDate() {
            let date = this.employee.processedAt ? new Date(this.employee.processedAt) : null;
            if (!date) {
                // Payrolls processed before processedAt was recorded: last day of the payroll month
                const monthIndex = monthNames.indexOf(this.employee.month);
                const year = Number(this.employee.year);
                if (monthIndex === -1 || !year) return "-";
                date = new Date(year, monthIndex + 1, 0);
            }
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        grossEarnings() {
            const totalDaysSalary = Number(this.employee.daysWorked* this.employee.perDaySalary) || 0;
            const bonus = Number(this.employee.bonus) || 0;
            const leavesEncashment = Number(this.employee.leavesEncashmentAmount) || 0;
            const transactionFees = Number(this.employee.transactionFees) || 0;
            const positiveAdjustment =
                (Number(this.employee.extraAmountAdjustment) > 0
                ? Number(this.employee.extraAmountAdjustment)
                : 0);
            const grossEarnings = totalDaysSalary + bonus + leavesEncashment + transactionFees + positiveAdjustment;
            return grossEarnings
        },
        totalDeductions() {
        const lateDeduction = Number(this.employee.lateDeduction) || 0;
        const taxDeduction = Number(this.employee.taxDeduction) || 0;
        const negativeAdjustment =
            (Number(this.employee.extraAmountAdjustment) < 0
            ? Math.abs(Number(this.employee.extraAmountAdjustment))
            : 0);

        return lateDeduction + taxDeduction + negativeAdjustment;
        }
    },
    methods: {
        formatCurrency(amount) {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "PKR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(amount || 0);
        },
        formatPeriod(month, year) {
            return `${month} ${year}`;
        },
        generateTransactionId() {
            const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
            let result = "";
            for (let i = 0; i < 8; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        },
        amountToWords(num) {
            return convertAmountToWords(num)
        },
        formatEmployeeId(employeeId) {
            if(!employeeId) return ""
            const suffix = employeeId.length >= 5 ? employeeId.slice(-5) : employeeId;
            return `emp_${suffix}`;
        },
        formatPayrollId(payrollId) {
            if(!payrollId) return ""
            const suffix = payrollId.length >= 5 ? payrollId.slice(-5) : payrollId;
            return `p_${suffix}`;
        },
        async downloadPayslip() {
            const payslipElement = this.$refs.payslip;
            if (!payslipElement) return;
            const { employeeName, month, year } = this.employee || {}

            const canvas = await html2canvas(payslipElement, {
                scale: 2, // Increase if quality is low
                useCORS: true,
            });

            const imgData = canvas.toDataURL('image/png');

            // A0 size in mm: 841 x 1189
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a0',
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            console.log("pdfWidht", pdfWidth)

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

            pdf.save(`${employeeName}_${month}_${year}_Payslip.pdf`);
        },
        async maybeDownloadPayslip() {
            const payslipId = this.$route.params.employeeId;
            if (payslipId) {
                const storageKey = `payslipData_${payslipId}`;
                const payslipData = JSON.parse(localStorage.getItem(storageKey));

                if (payslipData) {
                    this.employee = payslipData.employee
                    this.salaryData = payslipData.salaryData
                    this.employee.status = "Processed"
                    this.employee.employeeId = this.formatEmployeeId(this.employee.employeeId)

                    this.downloadedOnce = true; // prevent multiple downloads

                    await this.$nextTick(); // wait for DOM to update
                    
                    setTimeout(() => {
                        this.downloadPayslip();
                    }, 100);
                    setTimeout(() => {
                        if(this.employee){
                        localStorage.removeItem(storageKey);
                       }
                    }, 60000);
                }
            }
        }
    },
    mounted() {
        this.maybeDownloadPayslip();
    }
};
</script>

<style scoped>
/* Base Styles */
.calculation-breakdown {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: #F9FAFB;
    border-radius: 0.5rem;
}

.calculation-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
}

.calculation-label {
    font-size: 0.875rem;
    color: #4B5563;
    font-weight: bold;
}

.calculation-value {
    font-size: 0.875rem;
    font-weight: normal;
    color: #111827;
}

.calculation-note {
    font-size: 0.75rem;
    color: #6B7280;
    margin-bottom: 0.5rem;
    padding-left: 1rem;
    font-style: italic;
    max-width: 200px;
    word-wrap: break-word;
    white-space: pre-line; /* Preserve line breaks */
}

.payslip-container {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    max-width: 800px;
    margin: 10px auto;
    color: #374151;
}

.payslip {
    border: 1px solid #E5E7EB;
    padding: 2.5rem;
    background-color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 0.5rem;
    position: relative;
}

/* Header Styles */
.payslip-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #E5E7EB;
}

.company-info {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.company-logo {
    background-color: #36A9E1;
    border-radius: 0.375rem;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.company-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
}

.company-details {
    font-size: 0.875rem;
    color: #6B7280;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
}

.company-contact {
    font-size: 0.875rem;
    color: #6B7280;
    margin: 0;
}

.payslip-title {
    text-align: right;
}

.payslip-title h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.25rem 0;
    letter-spacing: 0.025em;
}

.payslip-period {
    font-size: 0.875rem;
    color: #6B7280;
    margin: 0 0 0.25rem 0;
}

.payslip-id {
    font-size: 0.75rem;
    color: #9CA3AF;
    margin: 0;
    font-family: 'Roboto Mono', monospace;
}

/* Employee Information */
.employee-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    margin-bottom: 1.5rem;
    padding: 1.25rem;
    background-color: #F9FAFB;
    border-radius: 0.375rem;
}

.employee-info h3 {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #36A9E1;
    margin: 0 0 0.75rem 0;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.detail-label {
    font-size: 0.875rem;
    color: #4b5563;
    font-weight: bold;
}

.detail-value {
    font-size: 0.875rem;
    color: #111827;
    font-weight: normal;
}

.status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.status-badge.processed {
    background-color: #D1FAE5;
    color: #065F46;
}

.status-badge.draft {
    background-color: #EFF6FF;
    color: #1E40AF;
}

.status-badge.pending {
    background-color: #FEF3C7;
    color: #92400E;
}

/* Salary Details */
.salary-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.25rem;
    margin-bottom: 1.5rem;
    padding: 1.25rem;
    padding-top: 1.25rem; 
    background-color: #F9FAFB;
    border-radius: 0.375rem;
    margin-top: 2rem;;
}

.section-header {
    margin-bottom: 1rem;
}

.section-header h3 {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #36A9E1;
    margin: 0 0 0.25rem 0;
}

.header-line {
    height: 1px;
    background-color: #E5E7EB;
}

.amount-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px dashed #E5E7EB;
}

.amount-row:last-child {
    border-bottom: none;
}

.amount-label {
    font-size: 0.875rem;
    color: #4B5563;
    font-weight: bold;
}

.amount-value {
    font-size: 0.875rem;
    font-weight: normal;
    color: #111827;
}

.amount-row.total {
    padding-top: 0.75rem;
    margin-top: 0.25rem;
}

.amount-row.total .amount-label,
.amount-row.total .amount-value {
    font-weight: 600;
    color: #111827;
}

/* Net Pay */
.net-pay {
    background-color: #60aacf35;
    border-radius: 0.375rem;
    padding: 1.5rem;
    margin: 2rem 0;
    color: #36A9E1;
}

.net-pay-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: -0.5rem;
}

.net-pay-label {
    font-size: 1rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.net-pay-amount {
    font-size: 1.5rem;
    font-weight: 700;
}

.net-pay-words {
    font-size: 0.75rem;
    opacity: 0.9;
    margin: 0;
    font-style: italic;
}

/* Attendance Summary */
.attendance-summary {
    margin-bottom: 2rem;
}

.attendance-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-top: 1rem;
}

.attendance-item {
    background-color: #F9FAFB;
    border-radius: 0.375rem;
    padding: 0.75rem;
    text-align: center;
}

.attendance-label {
    display: block;
    font-size: 0.75rem;
    color: #6B7280;
    margin-bottom: 0.25rem;
}

.attendance-value {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
}

/* Footer */
.payslip-footer {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #E5E7EB;
    font-size: 0.75rem;
    color: #6B7280;
}

.footer-notes {
    margin-bottom: 0.5rem;
}

.footer-notes p {
    margin: 0.25rem 0;
}

.footer-meta {
    display: flex;
    justify-content: space-between;
}

/* Action Buttons */
.payslip-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.action-btn svg {
    flex-shrink: 0;
}

.print-btn {
    background-color: #EFF6FF;
    color: #3B82F6;
}

.print-btn:hover {
    background-color: #DBEAFE;
}

.download-btn {
    background-color: #36A9E1;
    color: white;
}

.download-btn:hover {
    background-color: #4338CA;
}

/* No Data State */
.no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    color: #6B7280;
}

.no-data svg {
    margin-bottom: 1rem;
}

.no-data h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
}

.no-data p {
    margin: 0;
    max-width: 24rem;
}
</style>


// WEBPACK FOOTER //
// src/components/PayrollManagement/Payslip/PaySlip.vue